import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { reviewApi, saveBlob, COMMENT_MODE } from '../../utils/reviewApi';
import StartReviewModal from './StartReviewModal';
import { Avatar, FileChip, Modal, PeoplePicker, ProgressBar, dueText, fmtDate } from './reviewUi';
import '../../styles/DocReview.css';

const STATUS_PILL = {
  open: ['open', 'In review'],
  complete: ['done', 'Complete'],
  cancelled: ['cancel', 'Cancelled'],
};
const REVIEWER_PILL = {
  done: ['done', 'Done'],
  reviewing: ['open', 'Reviewing'],
  pending: ['none', 'Not opened'],
};

function AddReviewersModal({ review, onClose, onDone }) {
  const [people, setPeople] = useState(null);
  const [value, setValue] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    reviewApi.users().then((d) => {
      const have = new Set((review.reviewers || []).map((r) => r.user_id));
      setPeople((d.people || []).filter((p) => !have.has(p.user_id)));
    }).catch((e) => { setPeople([]); setError(e.message); });
  }, [review]);
  const save = async () => {
    if (!value.length) { onClose(); return; }
    setBusy(true);
    try {
      await reviewApi.addReviewers(review.id, value);
      onDone();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Modal title="Add reviewers" subtitle={`${review.name} · ${review.item_key} Rev ${review.revision}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={save} disabled={busy}>Add</button>
        </>
      )}>
      <PeoplePicker people={people} value={value} onChange={setValue} autoFocus />
      <div className="drv-small">They are notified, and see it under “Waiting for you”.</div>
      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}

function ReviewBlock({ review, focused, onChanged, onAddReviewers }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const p = review.progress || {};
  const due = dueText(review.due_date, p.days_left);
  const [pillCls, pillText] = STATUS_PILL[review.status] || STATUS_PILL.open;
  const open = review.status === 'open';
  const curRound = (review.rounds || []).find((r) => r.round_no === review.current_round);
  const roundOpen = curRound?.status === 'open';
  const closedRounds = (review.rounds || []).filter((r) => r.status === 'closed');

  const act = async (label, fn, confirmText) => {
    // eslint-disable-next-line no-alert
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(label);
    setError('');
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  // The review record (PDF) — the regulator's evidence of who reviewed what and how each
  // comment was resolved. Built server-side from this review only.
  const record = () => act('record', async () => {
    const { blob, name } = await reviewApi.recordBlob(review.id);
    saveBlob(blob, name);
  });

  if (review.status !== 'open') {
    return (
      <div className={`drv-review${focused ? ' drv-review--focus' : ''}`} id={`review-${review.id}`}>
        <div className="drv-review-head" style={{ borderBottom: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3>{review.name}</h3>
            <span className={`drv-pill drv-pill--${pillCls}`}>{pillText}</span>
            <span className="drv-small">
              started by {review.created_by_name} · {fmtDate(review.created_at)}
              {review.closed_at ? ` · closed ${fmtDate(review.closed_at)}` : ''}
              {' · '}{review.rounds?.length || 1} round{(review.rounds?.length || 1) === 1 ? '' : 's'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span className="drv-small">
              {p.reviewers_done} of {p.reviewers_total} reviewers · {p.comments_resolved} of {p.comments_total} comments resolved
            </span>
            <ProgressBar done={p.reviewers_done} total={p.reviewers_total} color="#17603A" />
            <Link className="drv-link" to={`/review/r/${review.id}`}>Open document</Link>
            <button type="button" className="drv-link" disabled={!!busy} onClick={record}
              title="Download the review record as a PDF">
              {busy === 'record' ? 'Preparing…' : 'Review record (PDF)'}
            </button>
            {review.can_manage && (
              <button type="button" className="drv-link" disabled={!!busy}
                onClick={() => act('reopen', () => reviewApi.setStatus(review.id, 'open'))}>Re-open</button>
            )}
          </div>
        </div>
        {error && <div className="drv-error" style={{ margin: '0 16px 12px' }} role="alert">{error}</div>}
      </div>
    );
  }

  return (
    <div className={`drv-review${focused ? ' drv-review--focus' : ''}`} id={`review-${review.id}`}>
      <div className="drv-review-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3>{review.name}</h3>
          <span className={`drv-pill drv-pill--${pillCls}`}>
            Round {review.current_round} {roundOpen ? 'in review' : 'closed'}
          </span>
          <span className="drv-small">
            started by {review.created_by_name} · {fmtDate(review.created_at)}
            {review.due_date ? ` · due ${fmtDate(review.due_date)}` : ''}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="drv-btn" to={`/review/r/${review.id}`}>Open document</Link>
          <button type="button" className="drv-btn" disabled={!!busy} onClick={record}
            title="Download the review record as a PDF">
            {busy === 'record' ? 'Preparing…' : 'Review record'}
          </button>
          {review.is_reviewer && open && roundOpen && (
            review.my_status === 'done'
              ? <button type="button" className="drv-btn" disabled={!!busy}
                  onClick={() => act('undone', () => reviewApi.done(review.id, false))}>Not done yet</button>
              : <button type="button" className="drv-btn drv-btn--primary" disabled={!!busy}
                  onClick={() => act('done', () => reviewApi.done(review.id, true))}>I’m done reviewing</button>
          )}
          {review.can_manage && (
            <>
              <button type="button" className="drv-btn" onClick={onAddReviewers}>+ Add reviewer</button>
              {roundOpen ? (
                <button type="button" className="drv-btn" disabled={!!busy}
                  onClick={() => act('close', () => reviewApi.closeRound(review.id),
                    `Close round ${review.current_round}? Reviewers can no longer comment on it.`)}>
                  Close round
                </button>
              ) : (
                <button type="button" className="drv-btn" disabled={!!busy}
                  onClick={() => act('round', () => reviewApi.newRound(review.id),
                    `Start round ${review.current_round + 1}? Every reviewer is asked to review again.`)}>
                  Start round {review.current_round + 1}
                </button>
              )}
              <button type="button" className="drv-btn drv-btn--primary" disabled={!!busy}
                onClick={() => act('complete', () => reviewApi.setStatus(review.id, 'complete'),
                  'Mark this review complete? Reviewers are told it is finished.')}>
                Complete review
              </button>
              <button type="button" className="drv-btn drv-btn--danger" disabled={!!busy}
                onClick={() => act('cancel', () => reviewApi.setStatus(review.id, 'cancelled'),
                  'Cancel this review? It is kept, but nobody is asked to review it any more.')}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="drv-error" style={{ margin: '12px 16px 0' }} role="alert">{error}</div>}

      <div className="drv-kpis">
        <div className="drv-kpi">
          <span>Reviewers done</span>
          <b>{p.reviewers_done} of {p.reviewers_total}</b>
          <ProgressBar wide done={p.reviewers_done} total={p.reviewers_total} />
        </div>
        <div className="drv-kpi">
          <span>Comments resolved</span>
          <b>{p.comments_resolved} of {p.comments_total}</b>
          <ProgressBar wide done={p.comments_resolved} total={p.comments_total} color="#17603A" />
        </div>
        <div className="drv-kpi">
          <span>Suggested changes</span>
          <b>{p.changes_accepted} accepted · {p.changes_open} waiting</b>
          <ProgressBar wide done={p.changes_total - p.changes_open} total={p.changes_total} color="#1F4E9E" />
        </div>
        <div className="drv-kpi">
          <span>Time left</span>
          <b style={due.tone === 'late' ? { color: '#9A3412' } : undefined}>
            {p.days_left == null ? '—' : p.days_left < 0 ? `${-p.days_left} days late`
              : `${p.days_left} day${p.days_left === 1 ? '' : 's'}`}
          </b>
        </div>
      </div>

      {review.message && (
        <div className="drv-small" style={{ margin: '0 16px 14px' }}>Message: “{review.message}”</div>
      )}

      <div className="drv-scroll">
        <table className="drv-table drv-table--compact">
          <thead><tr><th>Reviewer</th><th>Status</th><th>Comments</th><th>Open</th><th>Last activity</th></tr></thead>
          <tbody>
            {(review.reviewers || []).map((v) => {
              const [cls, text] = REVIEWER_PILL[v.status] || REVIEWER_PILL.pending;
              const last = v.done_at || v.first_opened_at;
              return (
                <tr key={v.user_id}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={v.display_name} />{v.display_name}
                    </span>
                  </td>
                  <td><span className={`drv-pill drv-pill--${cls}`}>{text}</span></td>
                  <td>{v.comments}</td>
                  <td>{v.open}</td>
                  <td className="drv-muted">{last ? fmtDate(last) : `Notified ${fmtDate(v.added_at)}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {closedRounds.filter((r) => r.round_no !== review.current_round || !roundOpen).map((r) => (
        <div className="drv-round" key={r.id}>
          <span className="drv-pill drv-pill--done">Round {r.round_no} closed</span>
          <span>
            {fmtDate(r.started_at)} – {fmtDate(r.closed_at)} · {r.comments} comment{r.comments === 1 ? '' : 's'}
            {r.comments ? `, ${r.comments - r.open} resolved` : ''}
            {r.changes_accepted ? ` · ${r.changes_accepted} changes accepted` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Show reviews (mockup screen 3): every review of every revision of one document — several
 * reviews per revision (D5), each with its own rounds and progress (D10).
 */
export default function ReviewItemPage() {
  const { itemId } = useParams();
  const [params] = useSearchParams();
  const setupId = params.get('setup') || '';
  const focusId = params.get('review') || '';
  // `?start=1` — arrived from the Orcanos form's "Start Review" button (browser extension):
  // open the Start review form once, if the server says this draft can be started.
  const autoStart = params.get('start') === '1';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(null);
  const [starting, setStarting] = useState(false);
  const [autoStarted, setAutoStarted] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await reviewApi.itemReviews(itemId, setupId));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [itemId, setupId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!focusId || !data) return;
    const el = document.getElementById(`review-${focusId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focusId, data]);

  useEffect(() => {
    if (!autoStart || autoStarted || !data) return;
    setAutoStarted(true);
    if (data.item?.can_start) setStarting(true);
  }, [autoStart, autoStarted, data]);

  const item = data?.item;
  // Arrived to start a review (`start=1`) but the server says it can't be started — say why,
  // instead of silently showing the page without the form.
  const startNotice = autoStart && data && !item?.can_start
    ? !item
      ? 'This document could not be read from your Doc Review panel, so a review cannot be started here.'
      : item.start_blocked === 'active_review'
        ? 'This document already has an open review. Complete or cancel it before starting another.'
        : !item.is_draft
          ? `A review can start only while the document is in Draft. Its routing state is ${item.routing_state || 'not Draft'}.`
          : 'You don’t have permission to route this document in Orcanos, so you can’t start a review of it.'
    : '';
  return (
    <div className="drv-page drv-page--narrow">
      <div className="drv-small"><Link className="drv-link" to="/review">← Doc Review</Link></div>
      {error && <div className="drv-error" role="alert">{error}</div>}
      {!data && !error && <div className="drv-muted">Loading reviews…</div>}
      {startNotice && <div className="drv-note" role="status" style={{ marginTop: 12 }}>{startNotice}</div>}

      {item && (
        <div className="drv-head" style={{ alignItems: 'flex-start' }}>
          <div>
            <div className="drv-eyebrow">{item.key}</div>
            <h1 className="drv-title" style={{ marginTop: 4 }}>{item.name}</h1>
            <p className="drv-sub" style={{ fontSize: 13 }}>
              Rev {item.revision || '—'}
              {item.routing_state && <> · routing state <strong style={{ color: '#3D2070' }}>{item.routing_state}</strong></>}
              {item.last_approved && <> · last approved Rev {item.last_approved}</>}
              {' · '}<FileChip fileName={item.file_name} kind={item.file_kind} />
              <span className="drv-file">{item.file_name}</span>
              {item.file_kind && <> · {COMMENT_MODE[item.file_kind]}</>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {setupId && item.is_draft && (
              // One open review per document at a time: shown, but disabled with the reason.
              <button type="button" className="drv-btn drv-btn--primary drv-btn--lg"
                disabled={!item.can_start} onClick={() => setStarting(true)}
                title={item.can_start ? 'Start a new review of this draft'
                  : item.start_blocked === 'active_review'
                    ? 'This document already has an open review — complete or cancel it first'
                    : 'You can’t start a review of this document'}>
                + Start another review
              </button>
            )}
            {item.url && (
              <a className="drv-btn drv-btn--lg" href={item.url} target="_blank" rel="noopener noreferrer">
                Open in Orcanos ↗
              </a>
            )}
          </div>
        </div>
      )}

      {(data?.revisions || []).map((g) => {
        const open = g.reviews.filter((r) => r.status === 'open').length;
        const done = g.reviews.filter((r) => r.status === 'complete').length;
        return (
          <section className="drv-card" key={g.revision} aria-label={`Rev ${g.revision}`}>
            <div className="drv-card-head">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h2>Rev {g.revision}{' '}
                  <span>{g.is_current && item?.routing_state ? item.routing_state.toLowerCase() : ''}
                    {g.reviews.length ? `${g.is_current && item?.routing_state ? ' · ' : ''}${g.reviews.length} review${g.reviews.length === 1 ? '' : 's'}` : ''}</span>
                </h2>
                {open > 0 && <span className="drv-pill drv-pill--open">{open} open</span>}
                {done > 0 && <span className="drv-pill drv-pill--done">{done} complete</span>}
                {!g.reviews.length && <span className="drv-pill drv-pill--none">No review yet</span>}
              </div>
            </div>
            {g.reviews.map((r) => (
              <ReviewBlock key={r.id} review={r} focused={r.id === focusId}
                onChanged={load} onAddReviewers={() => setAdding(r)} />
            ))}
          </section>
        );
      })}

      {adding && (
        <AddReviewersModal review={adding} onClose={() => setAdding(null)}
          onDone={() => { setAdding(null); load(); }} />
      )}
      {starting && item && (
        <StartReviewModal setupId={setupId} row={item} onClose={() => setStarting(false)}
          onStarted={() => { setStarting(false); load(); }} />
      )}
    </div>
  );
}
